import { appStorage, runtimeConfig } from './runtime';

/**
 * GR-001 - single composition point for product analytics.
 *
 * Provider: self-hosted Umami (decision registered as ADR GR-001 in docs/bible.html).
 * The tracker is cookieless and stores no client-side identifier, so aggregate
 * metrics do not depend on the consent banner (US0.7). Nothing in this module may
 * send personal data: see `sanitizeEventData`.
 */

type UmamiPayload = {
  website?: string;
  hostname?: string;
  url?: string;
  referrer?: string;
  title?: string;
  [key: string]: unknown;
};

type UmamiTracker = {
  track: (
    nameOrPayload?: string | ((payload: UmamiPayload) => UmamiPayload),
    data?: Record<string, unknown>
  ) => unknown;
};

type AnalyticsWindow = Window & { umami?: UmamiTracker };

export type AnalyticsEventData = Record<string, string | number | boolean>;

export const ANALYTICS_SCRIPT_ID = 'senti-analytics';
export const ANALYTICS_PENDING_KEY = 'senti.analytics.pending.v1';
const MAX_PENDING_EVENTS = 50;

type PendingEvent = { name: string; data?: AnalyticsEventData; path: string };
let pendingEvents: PendingEvent[] | undefined;
let flushing = false;

/**
 * Property names that must never leave the device. The check is substring based
 * and case insensitive so that `birthDate`, `user_email` or `placeName` are all
 * rejected without maintaining an exhaustive list.
 */
const FORBIDDEN_PROPERTY_FRAGMENTS = Object.freeze([
  'address',
  'birth',
  'city',
  'coord',
  'country',
  'dob',
  'email',
  'gender',
  'lat',
  'lon',
  'mail',
  'name',
  'phone',
  'place',
  'token',
  'user',
]);

const MAX_EVENT_PROPERTIES = 10;

const safeWindow = (): AnalyticsWindow | undefined =>
  typeof window !== 'undefined' ? (window as AnalyticsWindow) : undefined;

const safeDocument = (): Document | undefined =>
  typeof document !== 'undefined' ? document : undefined;

export const isForbiddenEventProperty = (key: string): boolean => {
  const normalized = key.toLowerCase();
  return FORBIDDEN_PROPERTY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
};

/**
 * Drops any property that could carry personal data and keeps only scalars.
 * Returning a new object guarantees callers cannot smuggle nested payloads.
 */
export const sanitizeEventData = (
  data?: Record<string, unknown>
): AnalyticsEventData | undefined => {
  if (!data) {
    return undefined;
  }

  const sanitized: AnalyticsEventData = {};

  Object.keys(data)
    .filter((key) => !isForbiddenEventProperty(key))
    .slice(0, MAX_EVENT_PROPERTIES)
    .forEach((key) => {
      const value = data[key];

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      }
    });

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

/**
 * Capacitor serves the app from a local scheme, so `location.hostname` and
 * `document.referrer` are not usable. Native traffic is reported under a stable
 * synthetic hostname per platform, which keeps web and native separable in the
 * dashboard without inventing a device identifier.
 */
const nativeHostname = (): string => `${runtimeConfig.platform}.app.local`;

/**
 * Path only - never the query string. App routes carry the OIDC authorization
 * code and state on `/signin-oidc`, and the Stripe return ids on the checkout
 * routes; none of that may reach the analytics provider.
 */
const currentPath = (): string => safeWindow()?.location?.pathname || '/';

const buildNativePayload = (payload: UmamiPayload): UmamiPayload => ({
  ...payload,
  hostname: nativeHostname(),
  url: currentPath(),
  referrer: '',
});

const tracker = (): UmamiTracker | undefined => safeWindow()?.umami;

export const isAnalyticsEnabled = (): boolean => runtimeConfig.analytics.enabled;

/**
 * Injects the tracker exactly once. Safe to call on every start-up and on
 * platforms where analytics is not configured (it becomes a no-op).
 */
export const initAnalytics = (): boolean => {
  const { enabled, scriptUrl, websiteId, hostUrl } = runtimeConfig.analytics;
  const doc = safeDocument();

  if (!enabled || !doc) {
    return false;
  }

  if (doc.getElementById(ANALYTICS_SCRIPT_ID)) {
    return false;
  }

  const script = doc.createElement('script');
  script.id = ANALYTICS_SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = scriptUrl;
  script.setAttribute('data-website-id', websiteId);
  // Keep query strings and fragments out of the collected URL: the OIDC callback
  // carries `code` and `state`, and the Stripe return carries payment ids.
  script.setAttribute('data-exclude-search', 'true');
  script.setAttribute('data-exclude-hash', 'true');
  // Native builds emit page views explicitly, because the tracker cannot derive
  // a meaningful URL or referrer from the Capacitor scheme.
  script.setAttribute('data-auto-track', runtimeConfig.isNativePlatform ? 'false' : 'true');

  if (hostUrl) {
    script.setAttribute('data-host-url', hostUrl);
  }

  script.addEventListener('load', () => {
    if (runtimeConfig.isNativePlatform) {
      trackPageView();
    }
    void flushPendingEvents();
  });

  (doc.head ?? doc.body)?.appendChild(script);

  return true;
};

export const trackPageView = (): void => {
  const umami = tracker();

  if (!umami) {
    return;
  }

  if (runtimeConfig.isNativePlatform) {
    umami.track(buildNativePayload);
    return;
  }

  umami.track();
};

const readPendingEvents = (): PendingEvent[] => {
  if (pendingEvents) return pendingEvents;

  try {
    const stored: unknown = JSON.parse(appStorage.session.getItem(ANALYTICS_PENDING_KEY) ?? '[]');
    pendingEvents = Array.isArray(stored) ? stored
      .filter((event): event is PendingEvent => Boolean(event) &&
        typeof event.name === 'string' && typeof event.path === 'string' &&
        event.path.startsWith('/') && !/[?#]/.test(event.path))
      .slice(0, MAX_PENDING_EVENTS)
      .map(event => ({ name: event.name, path: event.path, data: sanitizeEventData(event.data) })) : [];
  } catch {
    pendingEvents = [];
  }

  return pendingEvents;
};

const persistPendingEvents = (): void => {
  try {
    if (pendingEvents?.length) {
      appStorage.session.setItem(ANALYTICS_PENDING_KEY, JSON.stringify(pendingEvents));
    } else {
      appStorage.session.removeItem(ANALYTICS_PENDING_KEY);
    }
  } catch {
    // Memory still covers a slow tracker when browser storage is unavailable.
  }
};

const flushPendingEvents = async (): Promise<void> => {
  const umami = tracker();
  if (!isAnalyticsEnabled() || !umami || flushing) return;

  flushing = true;
  const queue = readPendingEvents();
  try {
    while (queue.length) {
      const event = queue[0];
      await umami.track(payload => ({
        ...(runtimeConfig.isNativePlatform ? buildNativePayload(payload) : payload),
        url: event.path,
        name: event.name,
        data: event.data,
      }));
      queue.shift();
      persistPendingEvents();
    }
  } catch {
    // Keep pending events for the next load or emission. Never reject into UI.
  } finally {
    flushing = false;
  }
};

/** Accepts an event for ordered delivery, including across an OIDC redirect. */
export const trackEvent = (name: string, data?: Record<string, unknown>): boolean => {
  if (!isAnalyticsEnabled() || !name) return false;

  const queue = readPendingEvents();
  if (queue.length >= MAX_PENDING_EVENTS) return false;

  queue.push({ name, data: sanitizeEventData(data), path: currentPath() });
  persistPendingEvents();
  void flushPendingEvents();
  return true;
};
